import React, { useState, useEffect, useMemo } from 'react';

import ReviewStatus from '../../../config/ReviewStatus';
import ConfirmationModal from '../../../uiComponents/ConfirmationModal';
import SuccessMessageModal from '../../SuccessMessageModal';
import LinkLabel from '../../../uiComponents/LinkLabel';
import './index.css'
import APIUtils from '../../../utils/APIUtils';
import SessionConstants from '../../../config/SessionConstants';
import { TableContainer, TableBody, Paper, Button as MuiButton } from '@mui/material';
import { AddCircleOutline, ReplayOutlined, DeleteOutline } from '@mui/icons-material';
import MuiTableHeader from '../../../uiComponents/MuiTable/MuiTableHeader';
import TableFilterConfig from '../../../uiComponents/MuiTable/TableFilterConfig';
import MuiTablePagination from '../../../uiComponents/MuiTable/MuiTablePagination';
import {stableSort,getComparator} from '../../../uiComponents/MuiTable/MuiHelper';
import { StyledPaper, StyledTable, StyledTableRow, StyledTableCell } from '../../../uiComponents/MuiTable/StyledTable.js';
import Loading from '../../Loading';
import { DateUtils } from '../../../utils/DateUtils.js';
import Format from '../../../utils/Format.js';
import { loadViewState, saveTableViewState } from '../../../utils/ProgramSummaryViewState.js';

// Shared action-button style: fixed width so the box never shifts, icon left/middle, text wrapped right.
const actionButtonSx = (color) => ({
  width: '108px',
  justifyContent: 'flex-start',
  textAlign: 'left',
  textTransform: 'none',
  lineHeight: 1.2,
  padding: '5px 10px',
  color,
  borderColor: color,
  // && bumps specificity above the global `button:hover` shadow so each button's glow matches its own color.
  '&&:hover': { borderColor: color, backgroundColor: 'transparent', boxShadow: `0 0 5px ${color}` },
  // Global 508 focus style sets border:none; keep a 1px border so the box doesn't shift.
  '&:focus-visible': { border: '1px solid var(--accent-primary-color)' },
});

export default function ProgramVariantsTable(props) {
  // Filter/sort/pagination saved when the user last drilled in from this program.
  const savedView = useMemo(
    () => (props.viewScope ? loadViewState(props.viewScope, props.programId).variants || {} : {}),
    [props.viewScope, props.programId]
  );
  const [rows,setRows] = useState(props.data);  //hold original dataset
  const [tableData,setTableData] = useState([]); //  
  const [isLoading,setIsLoading] = useState(false);
    //props for Modal component
    const [isOpen,setIsOpen] = useState(false);
    const [modalTitle,setModalTitle] = useState('');
    const [modalMessage,setModalMessage] = useState('');
    const [action,setAction] = useState('');
    const [variantReviewId,setVariantReviewId] = useState(null);
    const [variantName,setVariantName] = useState('');
    //props for Success Modal Component
    const [isSuccessModalOpen,setIsSuccessModalOpen] = useState(false);
    const [successMessage,setSuccessMessage] = useState([]);
  //props on sort table
    const [order,setOrder] = useState(savedView.order || 'asc');
    const [orderBy,setOrderBy] = useState(savedView.orderBy || 'variantName');
    //prop for paging
    const [page, setPage] = useState(savedView.page ?? 0);
    const [rowsPerPage, setRowsPerPage] = useState(savedView.rowsPerPage || 10);
  
    //props for filter     
    const [searchFilter, setSearchFilter] = useState(savedView.searchFilter || '');
    const [filterBy, setFilterBy] = useState(savedView.filterBy || '');
  
  // Note: tableData is populated by TableFilterConfig's onFilteredDataChange callback below,
  // which re-applies any active filter whenever `rows` changes (e.g. after a refresh).
  // Do not also set tableData directly from `rows` here, as it would race with and
  // override the re-applied filter.

  // Persist the current sort/pagination so a return to Program Dashboard restores it.
  useEffect(() => {
    if (!props.viewScope) return;
    saveTableViewState(props.viewScope, props.programId, 'variants', { order, orderBy, page, rowsPerPage });
  }, [props.viewScope, props.programId, order, orderBy, page, rowsPerPage]);

  // Keep a restored/active page valid: clamp to the last page when filtered data shrinks.
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(tableData.length / rowsPerPage) - 1);
    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [tableData, rowsPerPage, page]);

   //#region Action Buttons & event
  const actionButton= (rowInfo) => {
      //Note: To test the action buttons,you need to comment the below if statement
       if (rowInfo.currentReviewVersion == 1 && rowInfo.reviewStatus != ReviewStatus.CLOSED){
        return "";
       }      
        switch(rowInfo.reviewStatus){
          case ReviewStatus.CLOSED:
            return (
              <MuiButton
                variant="outlined"
                sx={actionButtonSx('var(--primary-color)')}
                startIcon={<AddCircleOutline />}
                aria-label="Create new review"
                onClick={()=>openModal(rowInfo,'create','Create New Review')}
              >
                Create<br/>review
              </MuiButton>
            );
          case ReviewStatus.DELETED:
            return (
              <MuiButton
                variant="outlined"
                sx={actionButtonSx('var(--accent-primary-color)')}
                startIcon={<ReplayOutlined />}
                aria-label="Reopen review"
                onClick={()=>openModal(rowInfo,'reopen','Reopen Review')}
              >
                Reopen<br/>review
              </MuiButton>
            );
          default:
            return (
              <MuiButton
                variant="outlined"
                sx={actionButtonSx('var(--error-color)')}
                startIcon={<DeleteOutline />}
                aria-label="Delete review"
                onClick={()=>openModal(rowInfo,'delete','Delete Review')}
              >
                Delete<br/>review
              </MuiButton>
            );
        }
       }
    ///Set Modal Properties 
  const  openModal = (rowInfo,action,title) => {
    const message = "Do you want to " + action + " review for variant " + rowInfo.variantName + "? " + 
                  ((action == "delete") ? "Doing this will remove all associated interpretations." : "");
    setIsOpen(true);
    setVariantReviewId(rowInfo.variantReviewId);
    setVariantName(rowInfo.variantName);
    setModalTitle(title);
    setModalMessage(message);
    setAction(action);

  }
  const onConfirmation=async()=>{    
         setIsLoading(true);
         setIsOpen(false);
         let formData = new FormData();
         formData.append('variantReviewId', variantReviewId);
         let successMessage=[];

        switch(action){
          case "create": successMessage.push(await APIUtils.createReview(formData));break;
          case "delete": successMessage.push(await APIUtils.deleteReview(formData));break;
          case "reopen": successMessage.push(await APIUtils.reopenReview(formData));break;
        }   
        setIsSuccessModalOpen(true);
        setIsLoading(false);
        setSuccessMessage(successMessage);
    }
  const onCancel = (e) => { setIsOpen(false); }
  const  onCloseSuccessModal=async()=> {
      setIsSuccessModalOpen(false);
      setIsLoading(true);
      //refresh data
      var variantResponse = await APIUtils.getVariantsByProgramId(sessionStorage.getItem(SessionConstants.PROGRAM_ID)); 
      setIsLoading(false);
        setRows(variantResponse);
  }
  //#endregion
  
  //#region Sorting 
  const handleRequestSort = (event, property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
  };
  //#endregion
  //#region on filter 
  const onFilterTypeChange = (event) => {
    //Search Filter Type Select
    setSearchFilter(event.target.value);
    setFilterBy(''); //clear any set filters on type change
    setTableData(rows); //reset filters to initial data when changing top level search filter type
  };
  //++++Filter By Gene or Condition+++
  const handleFilterByChange = (value) => {
    setFilterBy(value);
    setPage(0); //reset page to page 1
  };

  const handleFilteredDataChange = (filteredData) => {
    setTableData(filteredData);
    setPage(0); //reset page to page 1
  };
  const onClearFilters = () => {
    setSearchFilter('');
    setFilterBy('');
    setTableData(rows); //reset filters to initial data
    setPage(0); //reset page to page 1
  };
  //#endregion
  //#region Paging
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  //#endregion
  //#region create columns header array
  const columns = [
    {label: 'Variant', id: 'variantName', numeric: false, disablePadding: false},
    {label: 'Gene',id: 'gene', numeric: false, disablePadding: false},
    {label: 'Condition',id: 'potentialCondition', numeric: false, disablePadding: false},
    {label: 'Last Program Classification',id: 'programClassification', numeric: false, disablePadding: false},
    {label: 'Last Reviewed',id: 'closedDate', numeric: false, disablePadding: false},
    {label: 'Current Review Version',id: 'currentReviewVersion', numeric: false, disablePadding: false},
    {label: 'Interpretation Status',id: 'interpretationStatusString', numeric: false},
    {label: 'Action', id: 'reviewAction'},
  ];

  //#endregion
  return (
    <>
      <div className="tableFilterGrid">
        <TableFilterConfig 
          rows={rows}
          initialSearchFilter={savedView.searchFilter || ''}
          initialFilterBy={savedView.filterBy || ''}
          onFilterCriteriaChange={(type, value) => {
            if (props.viewScope) {
              saveTableViewState(props.viewScope, props.programId, 'variants', { searchFilter: type, filterBy: value });
            }
            setPage(0);
          }}
          onFilteredDataChange={(filtered) => {
            setTableData(filtered);
          }}
          filterConfig={[
            { type: 'gene', label: 'Gene(s)', field: 'gene' },
            { type: 'condition', label: 'Condition(s)', field: 'potentialCondition' },
          ]}
        />
      </div>
      <div>
        <StyledPaper>       
          <TableContainer component={Paper}>
            <StyledTable 
              aria-label="variant table" >
              <MuiTableHeader 
                headCells={columns} 
                order={order}  
                orderBy={orderBy} 
                onRequestSort={handleRequestSort}
              />
              <TableBody>
                {stableSort(tableData, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    return(
                    <StyledTableRow tabIndex={-1}  key={index}>
                      <StyledTableCell style={{width: '25%'}}>
                        <LinkLabel  
                          for={row.variantName}
                          path={'/variantdashboard/' + row.variantName}
                          text={row.variantName}
                          state={ {variantId: row.variantId,variantName: row.variantName,breadCrumbs: props.breadCrumbs,}}
                        />
                      </StyledTableCell>
                      <StyledTableCell className='gene' style={{width: '15%'}}>{Format.CommaSeparatedList(row.gene)}</StyledTableCell>
                      <StyledTableCell style={{width: '7%'}}>{Format.CommaSeparatedList(row.potentialCondition)}</StyledTableCell>
                      <StyledTableCell style={{width: '7%'}}>{row.programClassification}</StyledTableCell>
                      <StyledTableCell style={{width: '12.5%'}}>{DateUtils.FormatLocalDate(row.closedDate)}</StyledTableCell>
                      <StyledTableCell>{row.currentReviewVersion}</StyledTableCell>
                      <StyledTableCell style={{width: '25%'}}>{row.reviewStatus === ReviewStatus.DELETED ? "Deleted" : row.interpretationStatusString}</StyledTableCell>
                      <StyledTableCell style={{width: '12.5%'}}>{actionButton(row)}</StyledTableCell>
                    </StyledTableRow>
                    );
                })}
              </TableBody>
            </StyledTable>
          </TableContainer>
          <MuiTablePagination 
            rowsPerPage={rowsPerPage}
            page={page}
            count={tableData.length}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage} 
          />
        </StyledPaper>
      </div>
              
      <ConfirmationModal 
        title={modalTitle}
        message={modalMessage}
        onCancel={onCancel}
        onConfirmation={onConfirmation}
        onClose={onCancel}
        isOpen={isOpen}
      />
      <SuccessMessageModal 
        modalIsOpen={isSuccessModalOpen}
        closeModal={onCloseSuccessModal}
        message={successMessage}
      />
      <Loading isLoading={isLoading}/>
    </>
  );
}//end of ProgramVaiantsTable  